import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Package, Truck, CheckCircle, Warning, Trash, Check, X, WhatsappLogo, CaretRight, Wrench, ArrowRight, Printer, CurrencyEur, MagnifyingGlass, PencilSimple, DotsThree, CalendarPlus } from "@phosphor-icons/react";
import { useSearchParams, Link } from "react-router-dom";
import { RepairDialog } from "./Riparazioni";
import { whatsappOrderArrived } from "@/lib/whatsapp";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { printOrderReceipt } from "@/lib/printRepair";

const inputCls = "w-full px-3.5 py-2.5 rounded-md bg-background border border-input text-sm focus:outline-none focus:border-primary transition-colors duration-150";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5";

const fmtDate = (d) => { const p = new Date(d); const z = (n) => String(n).padStart(2, "0"); return `${z(p.getDate())}-${z(p.getMonth() + 1)}-${String(p.getFullYear()).slice(2)}`; };

const STATUS_META = {
  da_ordinare: { label: "Da ordinare", cls: "bg-blue-500/15 text-blue-400", icon: Package },
  ordinato: { label: "Ordinato", cls: "bg-cyan-500/15 text-cyan-400", icon: Check },
  arrivato: { label: "Arrivato", cls: "bg-amber-500/15 text-amber-500", icon: Truck },
  in_riparazione: { label: "In riparazione", cls: "bg-violet-500/15 text-violet-400", icon: Wrench },
  ritirato: { label: "Ritirato", cls: "bg-green-500/15 text-green-500", icon: CheckCircle },
};

const ORDER_PAGE_SIZE = 100;

function OrderPaymentsDialog({ order, open, onOpenChange }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (open && order) {
      setItems(null);
      api.get(`/orders/${order.id}/payments`).then((r) => setItems(r.data)).catch(() => setItems([]));
    }
  }, [open, order]);

  const kindLabel = (k) => (k === "acconto" ? "Acconto" : "Saldo");
  const methodLabel = (m) => ({ contanti: "Contanti", carta: "Carta", bonifico: "Bonifico", assegno: "Assegno", cash: "Cash", finanziamento: "Finanziamento", bonus_docenti: "Bonus Docenti" }[m] || m);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="order-payments-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold tracking-tight">Pagamenti in Prima Nota</DialogTitle>
          <DialogDescription className="sr-only">Dettaglio dei pagamenti registrati in prima nota per questo ordine</DialogDescription>
        </DialogHeader>
        {order && (
          <div className="space-y-4 mt-2">
            <div className="border border-border rounded-md px-4 py-3">
              <p className="text-sm font-bold">{order.client_name} — {order.article}</p>
            </div>
            {items === null && <p className="text-sm text-muted-foreground">Caricamento...</p>}
            {items !== null && items.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="order-payments-empty">Nessun movimento trovato in Prima Nota per questo ordine.</p>
            )}
            {(items || []).map((e) => (
              <div key={e.id} className="border border-border rounded-md px-4 py-3 space-y-1" data-testid={`order-payment-row-${e.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-primary">{kindLabel(e.order_kind)}</span>
                  <span className="text-xs text-muted-foreground">{fmtDate(e.date)}</span>
                </div>
                {(e.payments || []).map((p, i) => (
                  <p key={i} className="text-sm">{methodLabel(p.method)}: <b>€ {Number(p.amount).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</b></p>
                ))}
                <p className="text-[11px] text-muted-foreground">
                  {e.receipt_type === "fattura" ? "Fattura" : e.receipt_type === "scontrino" ? "Scontrino" : "Non contabilizzato (cash)"}{e.invoice_number ? ` n. ${e.invoice_number}` : ""} · inserito da {e.created_by}
                </p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OrderPaymentDialog({ order, kind, open, onOpenChange, onDone }) {
  const [method, setMethod] = useState("contanti");
  const [receipt, setReceipt] = useState("scontrino");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setMethod("contanti"); setReceipt("scontrino"); setInvoiceNumber(""); }
  }, [open]);

  if (!open || !order) return null;
  const amount = kind === "acconto"
    ? (order.deposit || 0)
    : Math.max(0, (order.price || 0) - (order.deposit_registered ? (order.deposit || 0) : 0));

  const submit = async () => {
    if (method !== "cash" && receipt === "fattura" && !invoiceNumber.trim()) {
      toast.error("Inserisci il numero della fattura");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/orders/${order.id}/register-payment`, {
        kind,
        method,
        receipt_type: method === "cash" ? undefined : receipt,
        invoice_number: method !== "cash" && receipt === "fattura" ? invoiceNumber.trim() : undefined,
        operator: localStorage.getItem("active_operator") || undefined,
      });
      toast.success(kind === "acconto" ? "Acconto registrato in Prima Nota" : "Pagamento registrato in Prima Nota");
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" data-testid="order-payment-dialog" onClick={() => onOpenChange(false)}>
      <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-xl font-extrabold tracking-tight">{kind === "acconto" ? "Registra acconto in Prima Nota" : "Registra in Prima Nota"}</h2>
        <div className="space-y-5 mt-4">
          <div className="border border-border rounded-md px-4 py-3">
            <p className="text-sm font-bold">{order.client_name} — {order.article}</p>
            <p className="font-heading text-2xl font-extrabold mt-2" data-testid="order-payment-amount">
              € {Number(amount).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
            </p>
            {kind === "saldo" && order.deposit > 0 && order.deposit_registered && (
              <p className="text-xs text-muted-foreground mt-1">Totale € {Number(order.price).toLocaleString("it-IT", { minimumFractionDigits: 2 })} − acconto già registrato</p>
            )}
          </div>
          <div>
            <label className={labelCls}>Metodo di pagamento</label>
            <div className="flex flex-wrap gap-2">
              {[["contanti", "Contanti"], ["carta", "Carta"], ["bonifico", "Bonifico"], ["assegno", "Assegno"], ["bonus_docenti", "Bonus Docenti"], ["finanziamento", "Finanziamento"], ["cash", "Cash"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setMethod(v)} data-testid={`order-payment-method-${v}`}
                  className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                    method === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {method !== "cash" && (
            <div>
              <label className={labelCls}>Documento</label>
              <div className="flex gap-2">
                {[["scontrino", "Scontrino"], ["fattura", "Fattura"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setReceipt(v)} data-testid={`order-receipt-${v}`}
                    className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                      receipt === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
                    {l}
                  </button>
                ))}
              </div>
              {receipt === "fattura" && (
                <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} data-testid="order-invoice-number-input"
                  className={`${inputCls} mt-3`} placeholder="Numero fattura (Es. 123/2026)" />
              )}
            </div>
          )}
          <button onClick={submit} disabled={saving} data-testid="order-payment-confirm"
            className="w-full py-3 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition-opacity duration-150">
            {saving ? "Registrazione..." : "Conferma registrazione"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Ordini() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState(() => (new URLSearchParams(window.location.search).get("apri") ? "" : "da_ordinare"));
  const loadSeq = useRef(0);
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientName, setClientName] = useState("");
  const [payInfo, setPayInfo] = useState(null);
  const [sortBy, setSortBy] = useState("date");
  const [clientPhone, setClientPhone] = useState("");
  const [article, setArticle] = useState("");
  const [price, setPrice] = useState("");
  const [deposit, setDeposit] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [assigneeId, setAssigneeId] = useState("");
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [priceEdit, setPriceEdit] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [converting, setConverting] = useState(null);
  const [orderPage, setOrderPage] = useState(1);
  const [paying, setPaying] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const STATUS_DOT = { da_ordinare: "bg-red-500 text-red-500", ordinato: "bg-cyan-400 text-cyan-400", arrivato: "bg-amber-500 text-amber-500", in_riparazione: "bg-violet-500 text-violet-500", ritirato: "bg-green-500 text-green-500" };
  const [orderSearch, setOrderSearch] = useState("");
  const [orderQ, setOrderQ] = useState("");
  const [searchAll, setSearchAll] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editArticle, setEditArticle] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editDeposit, setEditDeposit] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    api.get("/users/technicians").then((r) => {
      setTechnicians(r.data);
      if (r.data.length && !assigneeId) {
        const mich = r.data.find((t) => t.name.toLowerCase().includes("michelangelo"));
        setAssigneeId((mich || r.data[0]).id);
      }
    }).catch(() => {});
  }, []);

  const load = async () => {
    const seq = ++loadSeq.current;
    const params = {};
    if (orderQ) {
      params.q = orderQ;
      if (searchAll) params.include_ritirati = true;
    } else if (filter) params.status = filter;
    params.sort = sortBy;
    const { data } = await api.get("/orders", { params });
    if (seq === loadSeq.current) setOrders(data);
  };

  useEffect(() => { load().catch(() => {}); }, [filter, sortBy, orderQ, searchAll]);
  useEffect(() => { setOrderPage(1); }, [filter, assigneeFilter, sortBy, orderQ, searchAll]);

  useEffect(() => {
    const t = setTimeout(() => setOrderQ(orderSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [orderSearch]);

  const [searchParams] = useSearchParams();
  const openHandled = useRef(false);
  useEffect(() => {
    const target = searchParams.get("apri");
    if (target && !openHandled.current && orders.length > 0) {
      openHandled.current = true;
      setExpandedId(target);
      setTimeout(() => {
        document.querySelector(`[data-testid="order-card-${target}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
    }
  }, [searchParams, orders]);

  useEffect(() => {
    clearTimeout(debounce.current);
    const q = clientQuery.trim();
    if (q.length < 2 || selectedClient) { setClientResults([]); return; }
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients/search", { params: { phone: q, name: q } });
        setClientResults(data);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(debounce.current);
  }, [clientQuery, selectedClient]);

  const pickClient = (c) => {
    setSelectedClient(c);
    setClientName(c.name);
    setClientPhone(c.phone);
    setClientQuery(c.phone);
    setClientResults([]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!assigneeId) { toast.error("Assegna l'ordine a un dipendente: è obbligatorio"); return; }
    setSaving(true);
    try {
      await api.post("/orders", {
        client_id: selectedClient?.id || null,
        client_name: clientName,
        client_phone: clientPhone || clientQuery,
        article,
        assignee_id: assigneeId || null,
        price: price ? parseFloat(String(price).replace(",", ".")) : null,
        deposit: deposit ? parseFloat(String(deposit).replace(",", ".")) : null,
        notes,
        operator: localStorage.getItem("active_operator") || undefined,
      });
      toast.success("Ordine ricambio registrato");
      setSelectedClient(null); setClientName(""); setClientPhone(""); setClientQuery("");
      setArticle(""); setPrice(""); setNotes("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (o) => {
    setEditing(o);
    setEditName(o.client_name || "");
    setEditPhone(o.client_phone || "");
    setEditArticle(o.article || "");
    setEditPrice(o.price != null ? String(o.price).replace(".", ",") : "");
    setEditDeposit(o.deposit != null && o.deposit > 0 ? String(o.deposit).replace(".", ",") : "");
    setEditNotes(o.notes || "");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editName.trim()) { toast.error("Inserisci il nome del cliente"); return; }
    if (!editArticle.trim()) { toast.error("Inserisci l'articolo richiesto"); return; }
    setEditSaving(true);
    try {
      const payload = { client_name: editName.trim(), client_phone: editPhone.trim(), article: editArticle.trim(), notes: editNotes.trim() };
      const pv = editPrice.trim() ? parseFloat(String(editPrice).replace(",", ".")) : null;
      const dv = editDeposit.trim() ? parseFloat(String(editDeposit).replace(",", ".")) : null;
      if (pv != null && !Number.isNaN(pv)) payload.price = pv;
      if (dv != null && !Number.isNaN(dv)) payload.deposit = dv;
      await api.patch(`/orders/${editing.id}`, payload);
      toast.success("Ordine aggiornato");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditSaving(false);
    }
  };

  const reassign = async (o, newId) => {
    try {
      await api.patch(`/orders/${o.id}`, { assignee_id: newId || null });
      toast.success(newId ? "Ordine riassegnato" : "Assegnazione rimossa");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const savePrice = async (o) => {
    try {
      const value = priceEdit.trim() ? parseFloat(String(priceEdit).replace(",", ".")) : null;
      await api.patch(`/orders/${o.id}`, { price: value });
      toast.success("Prezzo aggiornato");
      setEditingPriceId(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const advance = async (o, status) => {
    try {
      await api.patch(`/orders/${o.id}`, { status });
      toast.success(status === "arrivato" ? `Ricambio arrivato: avvisa ${o.client_name}` : status === "ordinato" ? "Ordine segnato come ordinato" : "Ordine ritirato");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const arrivedOrders = orders.filter((o) => o.status === "arrivato");

  const gotoOrder = (o) => {
    setAssigneeFilter("");
    setFilter("");
    setExpandedId(o.id);
    setTimeout(() => {
      document.querySelector(`[data-testid="order-card-${o.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 600);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Ordine eliminato");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const arrivedCount = orders.filter((o) => o.status === "arrivato").length;

  return (
    <div className="p-8 space-y-8" data-testid="ordini-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Ricambi e accessori su ordinazione</p>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-none">Ordini Ricambi</h1>
      </div>

      {arrivedOrders.length > 0 && !filter && (
        <div className="border border-amber-500/40 bg-amber-500/10 rounded-md px-5 py-4" data-testid="orders-arrived-banner">
          <div className="flex items-center gap-3 mb-3">
            <Warning size={22} weight="duotone" className="text-amber-500 shrink-0" />
            <p className="text-sm font-semibold text-amber-500">
              {arrivedOrders.length} {arrivedOrders.length === 1 ? "ricambio arrivato" : "ricambi arrivati"} in attesa di ritiro — contatta il cliente.
            </p>
          </div>
          <ul className="space-y-1.5 ml-1" data-testid="banner-orders-list">
            {arrivedOrders.map((o) => (
              <li key={o.id} className="flex items-center gap-2">
                <button onClick={() => gotoOrder(o)} data-testid={`banner-order-${o.id}`}
                  className="text-sm text-amber-500 font-bold hover:underline underline-offset-2 transition-colors duration-150 text-left truncate">
                  {o.client_name} — {o.article}
                </button>
                {o.whatsapp_notified_at
                  ? <span className="ml-auto shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-500 text-[10px] font-bold uppercase tracking-wider" data-testid={`banner-notified-${o.id}`}><WhatsappLogo size={11} weight="fill" /> Avvisato</span>
                  : <span className="ml-auto shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-500 text-[10px] font-bold uppercase tracking-wider blink-red" data-testid={`banner-not-notified-${o.id}`}>Da avvisare</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={submit} className="border border-border rounded-md bg-card p-6" data-testid="order-form">
        <h2 className="font-heading font-bold tracking-tight mb-5">Nuovo ordine</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-5">
            <div className="relative">
              <label className={labelCls}>Cliente (cerca per nome o telefono)</label>
              <input value={clientQuery}
                onChange={(e) => { setClientQuery(e.target.value); setSelectedClient(null); }}
                data-testid="order-client-search-input" className={inputCls} placeholder="Es. Rossi oppure 333 1234567" />
              {clientResults.length > 0 && (
                <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-popover border border-border rounded-md" data-testid="order-client-results">
                  {clientResults.map((c) => (
                    <button key={c.id} type="button" onClick={() => pickClient(c)} data-testid={`order-client-result-${c.id}`}
                      className="w-full text-left px-4 py-2.5 hover:bg-accent transition-colors duration-150 flex justify-between items-center">
                      <span className="text-sm font-semibold">{c.name}</span>
                      <span className="text-xs text-muted-foreground">{c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Nome e Cognome *</label>
                <input required value={clientName} onChange={(e) => setClientName(e.target.value.replace(/\d/g, ""))}
                  data-testid="order-client-name-input" className={inputCls} placeholder="Es. Mario Rossi" />
              </div>
              <div>
                <label className={labelCls}>Telefono *</label>
                <input required inputMode="tel" value={clientPhone} onChange={(e) => setClientPhone(e.target.value.replace(/[^\d+]/g, ""))}
                  data-testid="order-client-phone-input" className={inputCls} />
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label className={labelCls}>Dipendente assegnato *</label>
              <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} data-testid="order-assignee-select" className={inputCls}>
                {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Articolo richiesto *</label>
              <input required value={article} onChange={(e) => setArticle(e.target.value)}
                data-testid="order-article-input" className={inputCls} placeholder="Es. Batteria iPhone 12" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Prezzo concordato (€)</label>
                <input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal"
                  data-testid="order-price-input" className={inputCls} placeholder="0,00" />
              </div>
              <div>
                <label className={labelCls}>Acconto (€)</label>
                <input value={deposit} onChange={(e) => setDeposit(e.target.value)} inputMode="decimal"
                  data-testid="order-deposit-input" className={inputCls} placeholder="0,00" />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-5">
            <div>
              <label className={labelCls}>Note</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                data-testid="order-notes-input" className={inputCls} placeholder="Colore, fornitore, dettagli..." />
            </div>
            <button type="submit" disabled={saving} data-testid="order-submit-button"
              className="w-full py-3.5 mt-auto rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition-opacity duration-150">
              {saving ? "Registrazione..." : "Registra ordine"}
            </button>
          </div>
        </div>
      </form>

      <div className="space-y-4">
        <div data-testid="orders-search-wrap">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-primary" />
            <input value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)}
              data-testid="orders-search-input"
              placeholder="Cerca un ordine: nome, cognome, telefono o articolo/dispositivo..."
              className="w-full pl-10 pr-9 py-2.5 rounded-md bg-background border border-primary/50 text-sm focus:outline-none focus:border-primary transition-colors duration-150" />
            {orderSearch && (
              <button type="button" onClick={() => setOrderSearch("")} title="Cancella la ricerca" data-testid="orders-search-clear"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150">
                <X size={14} weight="bold" />
              </button>
            )}
          </div>
          <div className="mt-1.5 flex items-center gap-4 flex-wrap">
            <label className="inline-flex items-center gap-2 text-[11px] font-semibold text-muted-foreground cursor-pointer select-none" data-testid="orders-search-all-wrap">
              <input type="checkbox" checked={searchAll} onChange={(e) => setSearchAll(e.target.checked)}
                data-testid="orders-search-all-checkbox" className="w-3.5 h-3.5 accent-primary" />
              Cerca anche tra gli ordini già ritirati
            </label>
            {orderQ && (
              <p className="text-[11px] text-primary font-semibold" data-testid="orders-search-hint">
                {searchAll ? `Ricerca su TUTTI gli ordini: risultati per "${orderQ}"` : `Ricerca solo tra gli ordini da consegnare: risultati per "${orderQ}"`}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {[["da_ordinare", "Da ordinare"], ["ordinato", "Ordinati"], ["arrivato", "Arrivati"], ["in_riparazione", "In riparazione"], ["ritirato", "Ritirati"], ["", "Tutti"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`orders-filter-${v || "tutti"}-button`}
                className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors duration-150 ${
                  filter === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="orders-sort-select"
              className="px-3 py-2 rounded-md bg-background border border-input text-sm font-semibold focus:outline-none focus:border-primary transition-colors duration-150">
              <option value="date">Ordina: data inserimento</option>
              <option value="chiusura">Ordina: data chiusura</option>
              <option value="alpha">Ordina: alfabetico</option>
            </select>
            <select value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)} data-testid="orders-assignee-filter"
              className="px-3 py-2 rounded-md bg-background border border-input text-sm font-semibold focus:outline-none focus:border-primary transition-colors duration-150">
              <option value="">Tutti i dipendenti</option>
              {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
        <div className="border border-border rounded-md bg-card" data-testid="orders-list">
          {orders.filter((o) => !assigneeFilter || o.assignee_id === assigneeFilter).length === 0 && (
            <div className="p-10 text-center text-muted-foreground text-sm" data-testid="orders-empty">Nessun ordine trovato</div>
          )}
          <div className="hidden md:grid md:grid-cols-[14px_10px_minmax(170px,210px)_minmax(0,1.25fr)_75px_75px_80px_220px] items-center gap-x-3 px-4 py-2 border-b border-border text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-bold" data-testid="orders-header">
            <span></span><span></span>
            <span>Cliente</span>
            <span>Articolo</span>
            <span className="text-right">Prezzo</span>
            <span className="text-right">Acconto</span>
            <span className="text-right">Da avere</span>
            <span className="text-right">Date · Azioni</span>
          </div>
          <div className="divide-y divide-border">
            {orders.filter((o) => !assigneeFilter || o.assignee_id === assigneeFilter)
              .slice((orderPage - 1) * ORDER_PAGE_SIZE, orderPage * ORDER_PAGE_SIZE)
              .map((o) => {
              const meta = STATUS_META[o.status];
              return (
                <div key={o.id}
                  onClick={() => setExpandedId(expandedId === o.id ? null : o.id)}
                  className={`px-3 py-2 sm:px-4 sm:py-2.5 cursor-pointer transition-colors duration-150 hover:bg-accent/40 ${expandedId === o.id ? "bg-accent/30" : ""}`}
                  data-testid={`order-card-${o.id}`}>
                  <div className="grid grid-cols-[auto_auto_minmax(0,1fr)] md:grid-cols-[14px_10px_minmax(170px,210px)_minmax(0,1.25fr)_75px_75px_80px_220px] items-center gap-x-2.5 md:gap-x-3 [grid-template-areas:'a_b_d'_'a_b_c'_'f_f_f'] md:[grid-template-areas:'a_b_d_c_g_h_i_f']">
                    <CaretRight size={14} className={`[grid-area:a] self-center shrink-0 text-muted-foreground transition-transform duration-150 ${expandedId === o.id ? "rotate-90" : ""}`} />
                    <span data-testid={`order-status-${o.id}`} title={meta.label}
                      className={`[grid-area:b] self-center justify-self-center w-2.5 h-2.5 rounded-full shadow-[0_0_8px_currentColor] ${STATUS_DOT[o.status] || "bg-muted-foreground text-muted-foreground"}`} />
                    <div className="[grid-area:c] min-w-0">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <span className="text-[13px] font-normal truncate">{o.article}</span>
                        <span className={`inline-flex items-center h-5 px-2 rounded text-[9px] font-bold uppercase tracking-wider shrink-0 ${meta.cls}`}>{meta.label}</span>
                      </div>
                      <div className="md:hidden flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {o.price != null && o.price > 0 && (
                          <span className="shrink-0 text-[11px] font-extrabold bg-secondary px-2 py-0.5 rounded-md border border-border">
                            € {Number(o.price).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                          </span>
                        )}
                        {o.deposit > 0 && (
                          <span className="shrink-0 text-[10px] font-bold text-amber-600 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/30">
                            Acc. € {Number(o.deposit).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="[grid-area:d] min-w-0 mt-0.5 md:mt-0" data-testid={`order-client-${o.id}`}>
                      <div className="flex items-baseline gap-2 min-w-0">
                        <span className="font-heading text-[14px] md:text-[15px] font-extrabold text-primary truncate">{o.client_name}</span>
                      </div>
                      {o.client_phone && <div className="text-[11px] font-mono text-muted-foreground mt-0.5">Tel: {o.client_phone}</div>}
                      {((o.status === "in_riparazione" && o.repair_status) || o.deposit_registered || o.payment_registered) && (
                        <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[10px] text-muted-foreground" data-testid={`order-meta-${o.id}`}>
                          {o.status === "in_riparazione" && o.repair_status && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold ${o.repair_status === "completato" ? "bg-green-500/15 text-green-500" : "bg-amber-500/15 text-amber-500"}`} data-testid={`order-repair-status-${o.id}`}>
                              <Wrench size={12} /> {o.repair_status === "completato" ? "Riparazione completata" : "Riparazione in lavorazione"}
                            </span>
                          )}
                          {o.deposit_registered && (
                            <button onClick={(e) => { e.stopPropagation(); setPayInfo(o); }} title="Vedi il pagamento registrato in Prima Nota"
                              data-testid={`pn-chip-acconto-${o.id}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-500/10 border border-green-500/30 text-green-600 font-bold hover:bg-green-500/20 transition-colors duration-150">
                              <CheckCircle size={12} weight="fill" /> Acconto in PN
                            </button>
                          )}
                          {o.payment_registered && (
                            <button onClick={(e) => { e.stopPropagation(); setPayInfo(o); }} title="Vedi il pagamento registrato in Prima Nota"
                              data-testid={`pn-chip-saldo-${o.id}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-500/10 border border-green-500/30 text-green-600 font-bold hover:bg-green-500/20 transition-colors duration-150">
                              <CheckCircle size={12} weight="fill" /> In prima nota
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="[grid-area:g] hidden md:block text-right text-xs font-extrabold" data-testid={`order-price-tag-${o.id}`}>
                      {o.price != null && o.price > 0 ? `€ ${Number(o.price).toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "—"}
                    </div>
                    <div className="[grid-area:h] hidden md:block text-right text-xs font-bold text-amber-600" data-testid={`order-deposit-tag-${o.id}`}>
                      {o.deposit > 0 ? `€ ${Number(o.deposit).toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "—"}
                    </div>
                    <div className="[grid-area:i] hidden md:block text-right text-xs font-extrabold text-red-500" data-testid={`order-due-${o.id}`}>
                      {o.price != null && o.price > 0 ? `€ ${Math.max(0, o.price - (o.deposit || 0)).toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "—"}
                    </div>
                    <div className="[grid-area:f] mt-1.5 md:mt-0 flex flex-col gap-2 md:items-end md:self-center">
                      <div className="flex items-center gap-3 flex-wrap md:justify-end">
                        <span className="inline-flex items-center gap-1 text-[9px] font-mono text-muted-foreground mr-0.5" title="Data di inserimento">
                          <CalendarPlus size={11} className="shrink-0" /> {fmtDate(o.created_at)}
                          {o.ordered_at && <span className="text-cyan-400 inline-flex items-center gap-1" title="Data ordine al fornitore" data-testid={`order-ordered-date-${o.id}`}> · <Package size={11} /> {fmtDate(o.ordered_at)}</span>}
                          {o.arrived_at && <span className="text-green-500 inline-flex items-center gap-1" title="Data arrivo ricambio"> · <CheckCircle size={11} /> {fmtDate(o.arrived_at)}</span>}
                        </span>
                        {o.whatsapp_notified_at && (
                          <span className="inline-flex items-center gap-0.5 text-green-500 text-[9px] font-mono" title={`Cliente avvisato su WhatsApp il ${fmtDate(o.whatsapp_notified_at)}`} data-testid={`order-notified-${o.id}`}>
                            <WhatsappLogo size={11} weight="fill" /> {fmtDate(o.whatsapp_notified_at)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap md:justify-end">
                      {o.status === "arrivato" && (
                        <button onClick={async (e) => { e.stopPropagation(); whatsappOrderArrived(o); try { await api.patch(`/orders/${o.id}`, { whatsapp_notified: true }); setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, whatsapp_notified_at: new Date().toISOString() } : x)); } catch { /* ignore */ } }} title="Avvisa il cliente su WhatsApp — ricambio arrivato"
                          data-testid={`whatsapp-order-${o.id}`}
                          className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-green-500/50 text-green-500 hover:bg-green-500/10 transition-colors duration-150 shrink-0">
                          <WhatsappLogo size={14} weight="fill" />
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); printOrderReceipt(o); }} title="Stampa ricevuta (80mm)" data-testid={`print-order-${o.id}`}
                        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md border border-border text-[10px] font-bold text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150">
                        <Printer size={14} /> Stampa
                      </button>
                      {o.status === "da_ordinare" && (
                        <button onClick={(e) => { e.stopPropagation(); advance(o, "ordinato"); }} data-testid={`mark-ordered-${o.id}`}
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-cyan-500/15 border border-cyan-500/40 text-cyan-400 text-[10px] font-bold hover:bg-cyan-500/25 transition-colors duration-150">
                          <Check size={13} /> Ordina
                        </button>
                      )}
                      {o.status === "ordinato" && (
                        <button onClick={(e) => { e.stopPropagation(); advance(o, "arrivato"); }} data-testid={`mark-arrived-${o.id}`}
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-500 text-[10px] font-bold hover:bg-amber-500/25 transition-colors duration-150">
                          <Truck size={13} /> Arrivato
                        </button>
                      )}
                      {o.status === "arrivato" && (
                        <button onClick={(e) => { e.stopPropagation(); advance(o, "ritirato"); }} data-testid={`mark-collected-${o.id}`}
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-green-500/15 border border-green-500/40 text-green-500 text-[10px] font-bold hover:bg-green-500/25 transition-colors duration-150">
                          <CheckCircle size={13} /> Ritirato
                        </button>
                      )}
                      <div className="relative">
                        <button onClick={(e) => { e.stopPropagation(); setMenuId(menuId === o.id ? null : o.id); }} title="Altre azioni"
                          data-testid={`order-menu-${o.id}`}
                          className="w-7 h-7 rounded-md border border-border inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150">
                          <DotsThree size={16} weight="bold" />
                        </button>
                        {menuId === o.id && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setMenuId(null); }} />
                            <div className="absolute right-0 top-full mt-1 z-50 min-w-[200px] rounded-md border border-border bg-secondary shadow-xl py-1" data-testid={`order-menu-panel-${o.id}`}>
                              <button onClick={(e) => { e.stopPropagation(); setMenuId(null); openEdit(o); }}
                                data-testid={`edit-order-${o.id}`}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150">
                                <PencilSimple size={14} /> Modifica ordine
                              </button>
                              {o.status === "arrivato" && (
                                <button onClick={(e) => { e.stopPropagation(); setMenuId(null); setConverting(o); }}
                                  data-testid={`convert-to-repair-${o.id}`}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-violet-400 hover:bg-violet-500/10 transition-colors duration-150">
                                  <Wrench size={14} /> Sposta in riparazione
                                </button>
                              )}
                              {o.deposit > 0 && !o.deposit_registered && (
                                <button onClick={(e) => { e.stopPropagation(); setMenuId(null); setPaying({ order: o, kind: "acconto" }); }}
                                  data-testid={`pay-deposit-${o.id}`}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-amber-500 hover:bg-amber-500/10 transition-colors duration-150 whitespace-nowrap">
                                  <CurrencyEur size={14} /> Acconto in Prima Nota
                                </button>
                              )}
                              {o.status === "ritirato" && o.price > 0 && !o.payment_registered && (
                                <button onClick={(e) => { e.stopPropagation(); setMenuId(null); setPaying({ order: o, kind: "saldo" }); }}
                                  data-testid={`pay-order-${o.id}`}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-green-500 hover:bg-green-500/10 transition-colors duration-150 whitespace-nowrap">
                                  <CurrencyEur size={14} /> Salda in Prima Nota
                                </button>
                              )}
                              {user?.role === "admin" && (
                                <button onClick={(e) => { e.stopPropagation(); setMenuId(null); remove(o.id); }}
                                  data-testid={`delete-order-${o.id}`}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-red-500 hover:bg-red-500/10 transition-colors duration-150">
                                  <Trash size={14} /> Elimina ordine
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      </div>
                    </div>
                  </div>
                  {expandedId === o.id && (
                    <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground space-y-1" data-testid={`order-expanded-${o.id}`}>
                      {o.notes && <p>Note: <span className="text-foreground">{o.notes}</span></p>}
                      <p>Cliente: <span className="text-foreground">{o.client_name}{o.client_phone ? ` — ${o.client_phone}` : ""}</span></p>
                      <p className="flex items-center gap-2 flex-wrap">Assegnato a:
                        <select value={o.assignee_id || ""} onChange={(e) => reassign(o, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`reassign-order-${o.id}`}
                          className="px-2.5 py-1.5 rounded-md bg-background border border-input text-xs font-semibold focus:outline-none focus:border-primary transition-colors duration-150">
                          <option value="">— Nessuno —</option>
                          {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </p>
                      <p className="flex items-center gap-2 flex-wrap">Prezzo concordato:
                        {editingPriceId === o.id ? (
                          <span className="inline-flex items-center gap-1.5">
                            <input onClick={(e) => e.stopPropagation()} value={priceEdit} onChange={(e) => setPriceEdit(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); savePrice(o); } }}
                              inputMode="decimal" placeholder="0,00" autoFocus
                              data-testid={`edit-price-input-${o.id}`}
                              className="w-24 px-2 py-1 rounded-md bg-background border border-input text-xs focus:outline-none focus:border-primary" />
                            <button onClick={(e) => { e.stopPropagation(); savePrice(o); }} data-testid={`save-price-${o.id}`}
                              className="text-green-500 hover:opacity-80 transition-opacity duration-150">
                              <Check size={15} weight="bold" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setEditingPriceId(null); }} data-testid={`cancel-price-${o.id}`}
                              className="text-muted-foreground hover:text-foreground transition-colors duration-150">
                              <X size={15} weight="bold" />
                            </button>
                          </span>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); setEditingPriceId(o.id); setPriceEdit(o.price != null ? String(o.price).replace(".", ",") : ""); }}
                            data-testid={`edit-price-${o.id}`}
                            className="text-foreground font-semibold hover:text-primary transition-colors duration-150"
                            title="Modifica prezzo">
                            {o.price != null ? `€ ${Number(o.price).toLocaleString("it-IT", { minimumFractionDigits: 2 })} ✎` : "Prezzo ✎"}
                          </button>
                        )}
                      </p>
                      {o.deposit != null && o.deposit > 0 && (
                        <p data-testid={`order-deposit-${o.id}`}>
                          Acconto: <span className="text-foreground font-bold">€ {Number(o.deposit).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span>
                          {o.price != null && (
                            <span className="text-foreground"> · Resta: <span className="font-bold text-amber-500">€ {Math.max(0, o.price - o.deposit).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span></span>
                          )}
                          {o.deposit_registered && <span className="text-green-500 font-bold"> · ✓ in Prima Nota</span>}
                        </p>
                      )}
                      {o.status === "in_riparazione" && o.repair_id && (
                        <p className="flex items-center gap-2 flex-wrap">
                          <Link to={`/riparazioni?apri=${o.repair_id}`} onClick={(e) => e.stopPropagation()} data-testid={`goto-linked-repair-${o.id}`}
                            className="inline-flex items-center gap-1 font-bold text-violet-400 hover:opacity-80 transition-opacity duration-150">
                            Apri la riparazione collegata <ArrowRight size={12} />
                          </Link>
                          {o.repair_status && (
                            <span className={`font-bold ${o.repair_status === "completato" ? "text-green-500" : "text-amber-500"}`} data-testid={`order-repair-status-detail-${o.id}`}>
                              ({o.repair_status === "completato" ? "completata" : "in lavorazione"})
                            </span>
                          )}
                        </p>
                      )}
                      <p>Registrato da: <span className="text-foreground">{o.created_by}</span></p>
                      {o.payment_registered && <p className="text-green-500 font-bold" data-testid={`order-paid-${o.id}`}>✓ Pagamento registrato in Prima Nota</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {(() => {
            const totalFiltered = orders.filter((o) => !assigneeFilter || o.assignee_id === assigneeFilter).length;
            const pages = Math.ceil(totalFiltered / ORDER_PAGE_SIZE);
            if (pages <= 1) return null;
            return (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border" data-testid="orders-pagination">
                <p className="text-xs text-muted-foreground" data-testid="orders-page-info">
                  Pagina {orderPage} di {pages} — {totalFiltered} ordini
                </p>
                <div className="flex items-center gap-1.5">
                  <button disabled={orderPage <= 1} onClick={() => setOrderPage(orderPage - 1)} data-testid="orders-page-prev"
                    className="px-3 h-8 rounded-md border border-border text-xs font-bold text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-40 transition-colors duration-150">
                    ← Prec.
                  </button>
                  <button disabled={orderPage >= pages} onClick={() => setOrderPage(orderPage + 1)} data-testid="orders-page-next"
                    className="px-3 h-8 rounded-md border border-border text-xs font-bold text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-40 transition-colors duration-150">
                    Succ. →
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      {editing && (
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" data-testid="order-edit-dialog" onClick={() => setEditing(null)}>
          <form onSubmit={saveEdit} onClick={(e) => e.stopPropagation()}
            className="bg-card border border-border rounded-lg p-6 w-full max-w-lg shadow-xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-extrabold tracking-tight">Modifica ordine</h2>
              <button type="button" onClick={() => setEditing(null)} data-testid="order-edit-close"
                className="w-8 h-8 rounded-md inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150">
                <X size={16} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Nome e Cognome *</label>
                <input required value={editName} onChange={(e) => setEditName(e.target.value.replace(/\d/g, ""))}
                  data-testid="order-edit-name-input" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Telefono</label>
                <input inputMode="tel" value={editPhone} onChange={(e) => setEditPhone(e.target.value.replace(/[^\d+]/g, ""))}
                  data-testid="order-edit-phone-input" className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Articolo richiesto *</label>
              <input required value={editArticle} onChange={(e) => setEditArticle(e.target.value)}
                data-testid="order-edit-article-input" className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Prezzo concordato (€)</label>
                <input value={editPrice} onChange={(e) => setEditPrice(e.target.value)} inputMode="decimal"
                  data-testid="order-edit-price-input" className={inputCls} placeholder="0,00" />
              </div>
              <div>
                <label className={labelCls}>Acconto (€)</label>
                <input value={editDeposit} onChange={(e) => setEditDeposit(e.target.value)} inputMode="decimal"
                  data-testid="order-edit-deposit-input" className={inputCls} placeholder="0,00" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Note</label>
              <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={3}
                data-testid="order-edit-notes-input" className={inputCls} placeholder="Colore, fornitore, dettagli..." />
            </div>
            <p className="text-[11px] text-muted-foreground">Stato e dipendente assegnato si cambiano direttamente dalla riga dell'ordine.</p>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setEditing(null)} data-testid="order-edit-cancel"
                className="flex-1 py-3 rounded-md border border-border text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors duration-150">
                Annulla
              </button>
              <button type="submit" disabled={editSaving} data-testid="order-edit-save"
                className="flex-1 py-3 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition-opacity duration-150">
                {editSaving ? "Salvataggio..." : "Salva modifiche"}
              </button>
            </div>
          </form>
        </div>
      )}
      <OrderPaymentDialog
        order={paying?.order}
        kind={paying?.kind}
        open={!!paying}
        onOpenChange={(v) => !v && setPaying(null)}
        onDone={load}
      />
      <OrderPaymentsDialog
        order={payInfo}
        open={!!payInfo}
        onOpenChange={(v) => !v && setPayInfo(null)}
      />
      <RepairDialog
        open={!!converting}
        onOpenChange={(v) => !v && setConverting(null)}
        technicians={technicians}
        initialClient={converting?.client_id ? { id: converting.client_id, name: converting.client_name, phone: converting.client_phone } : null}
        initialProblem={converting ? `Montaggio: ${converting.article}` : ""}
        initialTechnicianId={converting?.assignee_id || ""}
        initialPrice={converting?.price}
        initialOrderId={converting?.id || null}
        onCreated={async (repair) => {
          if (converting) {
            try { await api.patch(`/orders/${converting.id}`, { status: "in_riparazione", repair_id: repair?.id || null }); } catch { /* ignore */ }
          }
          load().catch(() => {});
        }}
      />
    </div>
  );
}
